import { useMutation, useQueryClient } from "react-query";
import { t } from "@/i18n-js/instance";
import type { Appearance } from "@/react/components/shared/BuilderUI/Store/interfaceSlice";
import { useToast } from "@/react/components/shared/uikit/ToastV2";
import { reactQueryDelete } from "@/react/helpers/backendRequestHelpers";
import { internalApi } from "@/react/helpers/urlHelpers";
import { useDeleteCustomThemeModal } from "./ThemeSelector/DeleteCustomThemeModal";
import type { SiteTheme } from "./constants";

const DEFAULT_THEMES_QUERY_KEY = ["themes"];

interface DeleteCustomThemeRequest {
  new_theme_id: number | null;
  page_id: number;
}

export interface DeleteCustomThemeResponse {
  appearance: Appearance;
  css_variables: SiteTheme["cssVariables"];
  custom_colors: SiteTheme["customColors"];
  id: number;
  is_custom: boolean;
  theme_name: string;
}

export const useDeleteCustomTheme = () => {
  const queryClient = useQueryClient();
  const { success, error } = useToast();
  const { hide: hideDeleteCustomThemeModal } = useDeleteCustomThemeModal();

  return useMutation<
    DeleteCustomThemeResponse,
    Error,
    { themeId: number } & DeleteCustomThemeRequest
  >(
    (data: { themeId: number } & DeleteCustomThemeRequest) =>
      reactQueryDelete(
        internalApi.settings.sites.themes.delete({ themeId: data.themeId }),
        {
          new_theme_id: data.new_theme_id,
          page_id: data.page_id,
        },
      ),
    {
      onSuccess: () => {
        void queryClient.invalidateQueries(DEFAULT_THEMES_QUERY_KEY);
        success(t("changes_saved"));
      },
      onError: () => {
        void hideDeleteCustomThemeModal();
        error(t("something_went_wrong"));
      },
    },
  );
};
